<template>
  <div class="tax-report-list">
    <router-view v-if="$route.name !== 'TaxreportList'"></router-view>
    <div v-if="$route.name === 'TaxreportList' || $route.name === 'ControlPanelVAT'" class="tax-report-list-wrap">
      <TitleBar>
        <template v-slot:title>
          <translate>VAT reports</translate>
        </template>
        <template v-slot:buttons>
          <GekkoButton
            :text="$gettext('New Report')"
            type="button"
            v-if="isAccountantUser"
            class="mr-2"
            @click="triggerNewReportModal"
          />
          <GekkoButton
            v-if="!$route.path.includes('control-panel')"
            :text="$gettext('All Reports')"
            icon="fa-solid fa-chart-column"
            type="button"
            variant="outline-primary"
            :to="{ name: 'Reports' }"
          />
        </template>
      </TitleBar>
      <ReportsVATNewModal
        v-if="showNewReportModal"
        @close="onNewReportClose"
        @submit="onNewReportSubmit"
        :financialYears="financialYears"
        :year="year"
        :quarter="quarter"
        :month="month"
        :consolidated="false"
      />
      <div class="content">
        <!-- View Toggle -->
        <template v-if="shownReports === 'company'">
          <div class="view-toggle-container mb-3">
            <div class="btn-group" role="group">
              <GekkoButton
                :text="$gettext('Quarterly View')"
                type="button"
                :variant="reportViewMode === 'quarterly' ? 'primary' : 'outline-primary'"
                @click="reportViewMode = 'quarterly'"
              />
              <GekkoButton
                :text="$gettext('Monthly View')"
                type="button"
                :variant="reportViewMode === 'monthly' ? 'primary' : 'outline-primary'"
                @click="reportViewMode = 'monthly'"
              />
            </div>
          </div>
          <section v-for="aYearOfReports in getFilteredReportsByYear" :key="aYearOfReports.id">
              <h2>{{ aYearOfReports.year }}</h2>
              <div :class="{ 'report-quarter-groups-wrap': reportViewMode === 'monthly' }">
                <div
                  v-for="group in getDisplayGroups(aYearOfReports.reports)"
                  :key="group.key"
                  :class="{
                    'report-quarter-group': group.monthly,
                    'report-quarter-group--conflict': group.monthly && hasConflictInGroup(group, aYearOfReports.rawReports)
                  }"
                >
                  <div v-if="group.monthly" class="report-quarter-group__title">
                    {{ group.quarter }}
                  </div>
                  <div class="report-cards-wrap" :class="{ 'report-cards-wrap--monthly-group': group.monthly }">
                  <div 
                    v-for="report in group.reports" 
                    :key="report.id || `${report.quarter}-${report.month}`"
                    class="report-card-item"
                  >
                  <!-- Existing Report Card -->
                  <div
                    v-if="!report.blank"
                    class="report-card"
                    @click="$router.push(controlPanelViewUrl(report.id))"
                  >
                    <div class="report-card__body">
                      <!-- Status Badge for Processing Reports -->
                      <div v-if="task_reports.includes(report.id)" class="report-card__status">
                        <div class="status-badge status-processing">
                          <div
                            class="status-circle-badge badge-spinner"
                            style="width: 13px; height: 13px"
                          />
                          {{ $gettext("Processing") }}
                        </div>
                      </div>

                      <!-- Icon -->
                      <div class="report-card__icon report-card__icon--done">
                        <font-awesome-icon icon="fa-regular fa-circle-check" />
                      </div>

                      <!-- Title and Description -->
                      <div>
                        <div class="report-card__title">
                          {{ reportViewMode === 'quarterly' ? report.quarter : getMonth(report.month) }}
                          {{ aYearOfReports.year }}
                        </div>
                        <div class="report-card__description">
                          {{ $gettext('Calculated on:') }} {{ formatDate(report.modified) }}
                        </div>
                        <div class="report-card__total">
                          <span class="report-card__total-label">{{ $gettext('Total:') }}</span>
                          <span v-if="getReportTotal(report) !== null">{{ getReportTotal(report) | currency('EUR', 0) }}</span>
                          <span v-else>-</span>
                        </div>
                      </div>
                    </div>

                    <div class="report-card__actions">
                      <b-dropdown
                        right
                        no-caret
                        toggle-tag="button"
                        variant="link"
                        size="sm"
                        class="report-card__action-overflow ui-dashboard-menu-dropdown"
                        toggle-class="report-card__action-overflow-toggle"
                      >
                        <template #button-content>
                          <font-awesome-icon icon="fa-solid fa-ellipsis" />
                        </template>
                        <b-dropdown-item-button @click="downloadPDF(report.id)">
                          <font-awesome-icon icon="fa-solid fa-download" class="mr-2" />
                          {{ $gettext('Download') }}
                        </b-dropdown-item-button>
                        <b-dropdown-item-button
                          v-gk-confirm="{
                            title: $gettext('Recalculate report'),
                            message: $gettext('This will overwrite your current VAT report and will take into account any changes since the last calculation. Your old VAT report will be deleted.'),
                            options: {
                              warning: $gettext('If you have already submitted this report to the tax authorities, it is recommended not to recalculate this report to stay consistent. Any changes can also be submitted in later quarters. Please check with your accountant if you should update this report.'),
                              variant: 'primary',
                              confirmText: $gettext('Recalculate')
                            },
                            callback: () => recalculateReport(report)
                          }"
                        >
                          <font-awesome-icon icon="fa-solid fa-rotate-right" class="mr-2" />
                          {{ $gettext('Recalculate') }}
                        </b-dropdown-item-button>
                      </b-dropdown>
                    </div>
                  </div>

                  <!-- Blank Report Card (New Report Button) -->
                  <div 
                    v-else-if="isConflictingBlank(report, aYearOfReports.rawReports)"
                    v-gk-confirm="getConflictGenerateConfirm(report, aYearOfReports.rawReports)"
                    class="report-card report-card--blank"
                    :class="{ 'report-card--conflict': !allowConflictingClick }"
                  >
                    <div class="report-card__body">
                      <div
                        class="report-card__icon"
                        :class="'report-card__icon--add'"
                      >
                        <i class="fa fa-plus-circle"></i>
                      </div>
                      <div>
                        <div class="report-card__title">
                          {{ reportViewMode === 'quarterly' ? report.quarter : getMonth(report.month) }}
                          {{ aYearOfReports.year }}
                        </div>
                        <div v-if="allowConflictingClick" class="report-card__description">
                          {{ $gettext('Generate new report') }}
                        </div>
                        <div
                          v-if="reportViewMode === 'monthly' && getMonthlyConflictWarning(report.month, aYearOfReports.rawReports)"
                          class="report-card__relation-warning"
                        >
                          {{ getMonthlyConflictWarning(report.month, aYearOfReports.rawReports) }}
                        </div>
                        <div
                          v-if="reportViewMode === 'quarterly' && getQuarterlyConflictWarning(report.quarter, aYearOfReports.rawReports)"
                          class="report-card__relation-warning"
                        >
                          {{ getQuarterlyConflictWarning(report.quarter, aYearOfReports.rawReports) }}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div 
                    v-else
                    @click="preselectedGenerateReportModal(report.year, report.quarter, report.month)"
                    class="report-card report-card--blank"
                  >
                    <div class="report-card__body">
                      <div
                        class="report-card__icon"
                        :class="'report-card__icon--add'"
                      >
                        <i class="fa fa-plus-circle"></i>
                      </div>
                      <div>
                        <div class="report-card__title">
                          {{ reportViewMode === 'quarterly' ? report.quarter : getMonth(report.month) }}
                          {{ aYearOfReports.year }}
                        </div>
                        <div v-if="!isConflictingBlank(report, aYearOfReports.rawReports) || allowConflictingClick" class="report-card__description">
                          {{ $gettext('Generate new report') }}
                        </div>
                        <div
                          v-if="reportViewMode === 'monthly' && getMonthlyConflictWarning(report.month, aYearOfReports.rawReports)"
                          class="report-card__relation-warning"
                        >
                          {{ getMonthlyConflictWarning(report.month, aYearOfReports.rawReports) }}
                        </div>
                        <div
                          v-if="reportViewMode === 'quarterly' && getQuarterlyConflictWarning(report.quarter, aYearOfReports.rawReports)"
                          class="report-card__relation-warning"
                        >
                          {{ getQuarterlyConflictWarning(report.quarter, aYearOfReports.rawReports) }}
                        </div>
                      </div>
                    </div>
                  </div>
                  </div>
                </div>
              </div>
              </div>
          </section>
        </template>
      </div>
    </div>
  </div>
</template>
<script>
import ReportsVATNewModal from '@/components/ReportsVATNewModal'
import settingsPageSetup from '@/mixins/settingsPageSetup'
import ReportsService from '@/store/services/reports.service'
import CompanyService from '@/store/services/company.service'
import GekkoButton from '@/components/GekkoButton'
import TitleBar from '@/components/TitleBar'
import { promptDownload } from '@/utils'
import { mapGetters } from 'vuex'

export default {
  name: 'TaxreportList',
  components: { TitleBar, ReportsVATNewModal, GekkoButton },
  mixins: [settingsPageSetup],
  computed: {
    ...mapGetters({
      getCompanyId: 'main/getCompanyId',
      currentCompany: "main/getCurrentCompany",
    }),
    getFilteredReportsByYear() {
      if (this.reportViewMode === 'quarterly') {
        return this.reportsByYear.map(yearGroup => ({
          ...yearGroup,
          reports: this._filterQuarterlyReports(yearGroup.reports),
          rawReports: yearGroup.reports
        }))
      } else {
        return this.reportsByYear.map(yearGroup => ({
          ...yearGroup,
          reports: this._filterMonthlyReports(yearGroup.reports),
          rawReports: yearGroup.reports
        }))
      }
    }
  },
  beforeDestroy() {
    clearInterval(this.checkInterval)
  },
  watch: {
    $route(to) {
      if (to.name === 'TaxreportList' || to.name === 'ControlPanelVAT') {
        this.fetchReports()
      }
    },
  },
  data() {
    return {
      pageSettings: {
        title: this.$gettext('Reports'),
        showCompanyPicker: true,
      },
      checkInterval: null,
      task_reports: [],
      isAccountantUser: true,
      shownReports: '',
      reportViewMode: 'quarterly',
      reportsByYear: [],
      reports: [],
      companyReports: [],
      financialYears: [],
      showNewReportModal: false,
      allowConflictingClick: true, // Set to true to allow generating reports on conflicting periods
      year: null,
      quarter: null,
      month: null,
      MONTHS: [
        { text: this.$gettext('Jan'), fullText: this.$gettext('January'), value: { month: 1, quarter: 'Q1' } },
        { text: this.$gettext('Feb'), fullText: this.$gettext('February'), value: { month: 2, quarter: 'Q1' } },
        { text: this.$gettext('Mar'), fullText: this.$gettext('March'), value: { month: 3, quarter: 'Q1' } },
        { text: this.$gettext('Apr'), fullText: this.$gettext('April'), value: { month: 4, quarter: 'Q2' } },
        { text: this.$gettext('May'), fullText: this.$gettext('May'), value: { month: 5, quarter: 'Q2' } },
        { text: this.$gettext('Jun'), fullText: this.$gettext('June'), value: { month: 6, quarter: 'Q2' } },
        { text: this.$gettext('Jul'), fullText: this.$gettext('July'), value: { month: 7, quarter: 'Q3' } },
        { text: this.$gettext('Aug'), fullText: this.$gettext('August'), value: { month: 8, quarter: 'Q3' } },
        { text: this.$gettext('Sep'), fullText: this.$gettext('September'), value: { month: 9, quarter: 'Q3' } },
        { text: this.$gettext('Oct'), fullText: this.$gettext('October'), value: { month: 10, quarter: 'Q4' } },
        { text: this.$gettext('Nov'), fullText: this.$gettext('November'), value: { month: 11, quarter: 'Q4' } },
        { text: this.$gettext('Dec'), fullText: this.$gettext('December'), value: { month: 12, quarter: 'Q4' } },
      ],
    }
  },
  created() {
    this.shownReports = 'company'
    this.fetchReports()
  },
  methods: {
    formatDate(value) {
      return new Date(value).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    },
    getMonth(month) {
      return this.MONTHS.find(m => m.value.month == month)?.text
    },
    fetchReports() {
      Promise.all([
        ReportsService.list({ company: this.getCompanyId }),
        CompanyService.financiallyActiveYears(this.getCompanyId),
      ])
        .then((responses) => {
          this.reports = responses[0].data
          this.financialYears = responses[1].data
          this.companyReports = this.reports.filter((reports) => reports.consolidated === false)
          this.reportsByYear = this._reorganizeReports(this.financialYears, this.companyReports)
          this.checkReportStatus()
        })
        .catch((error) => {
          console.log(error)
        })
    },
    checkReportStatus(){
      clearInterval(this.checkInterval)
      this.fetchingData = true
      this.checkInterval = setInterval(() => {
        ReportsService.status().then((response)=>{
          this.task_reports = response.data;
          if(response.data.length === 0){
            clearInterval(this.checkInterval)
          }
        })
      }, 5 * 1000);
    },
    triggerNewReportModal(){
      this.year = new Date().getFullYear() // Preselect current year
      this.quarter = null
      this.month = null
      this.showNewReportModal = true
    },
    preselectedGenerateReportModal(year, quarter, month) {
      this.year = year
      this.quarter = quarter
      this.month = month !== null && month !== undefined ? String(month) : null
      // this.showNewReportModal = true

      let report_initial_values = {
        company: this.currentCompany.id,
        year: year,
        month: month,
        quarter: quarter,
        consolidated: false,
        status: 'creating'
      }
      this.onNewReportSubmit(report_initial_values)
    },
    confirmConflictingGenerateReport(report) {
      this.preselectedGenerateReportModal(report.year, report.quarter, report.month)
    },
    getConflictGenerateConfirm(report, rawReports) {
      const relatedReportType = this.reportViewMode === 'monthly' ? this.$gettext('quarterly') : this.$gettext('monthly')
      const warning =
        this.reportViewMode === 'monthly'
          ? this.getMonthlyConflictWarning(report.month, rawReports)
          : this.getQuarterlyConflictWarning(report.quarter, rawReports)

      return {
        title: this.$gettext('Related report exists'),
        message: this.$gettext(
          'There is already a related %reportType% VAT report for this period. Do you still want to generate a new report?'
        ).replace('%reportType%', relatedReportType),
        options: {
          warning,
          variant: 'primary',
          confirmText: this.$gettext('Generate report'),
        },
        callback: this.confirmConflictingGenerateReport,
        callbackParams: report,
      }
    },
    _reorganizeReports(years, reports) {
      var outputReports = []
      for (var i = 0; i < years.length; i++) {
        var thisYear = years[i]
        var thisYearReports = {
          year: thisYear,
          reports: {
            Q1: { quarter: 'Q1', month: null, year: thisYear, blank: true },
            Q2: { quarter: 'Q2', month: null, year: thisYear, blank: true },
            Q3: { quarter: 'Q3', month: null, year: thisYear, blank: true },
            Q4: { quarter: 'Q4', month: null, year: thisYear, blank: true },
          },
        }
        for (var j = reports.length - 1; j > -1; j--) {
          if (reports[j].quarter === 'total Q123' || reports[j].quarter === 'delta Q123') {
            reports.splice(j, 1)
          } else {
            if (reports[j].year === parseInt(thisYear)) {
              if (reports[j].month) {
                if (thisYearReports.reports[reports[j].quarter]) {
                  delete thisYearReports.reports[reports[j].quarter]
                  var base = 3 * (reports[j].quarter[1] - 1)
                  thisYearReports.reports[base + 1] = {
                    quarter: reports[j].quarter,
                    month: base + 1,
                    year: thisYear,
                    blank: true,
                  }
                  thisYearReports.reports[base + 2] = {
                    quarter: reports[j].quarter,
                    month: base + 2,
                    year: thisYear,
                    blank: true,
                  }
                  thisYearReports.reports[base + 3] = {
                    quarter: reports[j].quarter,
                    month: base + 3,
                    year: thisYear,
                    blank: true,
                  }
                }
                thisYearReports.reports[reports[j].month] = reports[j]
              } else {
                reports[j].month = null
                thisYearReports.reports[reports[j].quarter] = reports[j]
              }
            }
          }
        }
        outputReports.push(thisYearReports)
      }
      return outputReports
    },
    _orderedReports(reports) {
      // Ordered ascending: Q1 -> Q4 and Jan -> Dec
      const reportsArray = Object.values(reports)
      const quarterOrder = (quarter) => Number(String(quarter || '').replace('Q', '')) || 0

      reportsArray.sort((a, b) => {
        const aHasMonth = a.month !== null && a.month !== undefined
        const bHasMonth = b.month !== null && b.month !== undefined

        if (aHasMonth && bHasMonth) {
          return a.month - b.month
        }

        if (!aHasMonth && !bHasMonth) {
          return quarterOrder(a.quarter) - quarterOrder(b.quarter)
        }

        return aHasMonth ? 1 : -1
      })

      return reportsArray
    },
    onNewReportSubmit(report_initial_values) {
      this.showNewReportModal = false
      this.$store.commit('taxreports/setReportInitialValues', report_initial_values)
      if (this.$route.path.includes('control-panel'))
        this.$router.push({ name: 'ControlPanelCreateVAT', params: { customerId: this.getCompanyId } })
      else this.$router.push({ name: 'CreateTaxreport' })
    },
    onNewReportClose() {
      this.showNewReportModal = false
    },
    controlPanelViewUrl(reportId) {
      if (this.$route.path.includes('control-panel'))
        return { name: 'ControlPanelViewVAT', params: { customerId: this.getCompanyId, id: reportId } }
      else return { name: 'ViewTaxreport', params: { id: reportId } }
    },
    downloadPDF(reportId) {
      ReportsService.getPDFBlob(reportId)
        .then((response) => {
          promptDownload(response)
        })
        .catch(() => {
          this.$store.commit('setAlert', {
            title: this.$gettext('Download failed'),
            text: this.$gettext('Could not download this report. Please try again.'),
            variant: 'danger',
            duration: 4,
          })
        })
    },
    recalculateReport(report) {
      this.preselectedGenerateReportModal(report.year, report.quarter, report.month)
    },
    getReportTotal(report) {
      const candidates = [
        report.total,
        report.total_vat,
        report.total_amount,
        report.balance,
        report.btw_total,
      ]
      const total = candidates.find((value) => value !== null && value !== undefined)
      if (total === undefined) {
        return null
      }
      const parsedTotal = Number(total)
      return Number.isFinite(parsedTotal) ? parsedTotal : null
    },
    _filterQuarterlyReports(reports) {
      // Always show all 4 quarters (Q1, Q2, Q3, Q4) even if blank
      const result = {
        Q1: reports.Q1 || { quarter: 'Q1', month: null, year: reports.Q1?.year, blank: true },
        Q2: reports.Q2 || { quarter: 'Q2', month: null, year: reports.Q2?.year, blank: true },
        Q3: reports.Q3 || { quarter: 'Q3', month: null, year: reports.Q3?.year, blank: true },
        Q4: reports.Q4 || { quarter: 'Q4', month: null, year: reports.Q4?.year, blank: true },
      }
      // Extract year from any report in this year's data
      const sampleReport = Object.values(reports)[0]
      if (sampleReport?.year && !result.Q1.year) {
        result.Q1.year = result.Q2.year = result.Q3.year = result.Q4.year = sampleReport.year
      }
      return result
    },
    _filterMonthlyReports(reports) {
      // Always show all 12 months (1-12) even if blank
      const result = {}
      const sampleReport = Object.values(reports)[0]
      const year = sampleReport?.year
      
      for (let month = 1; month <= 12; month++) {
        result[month] = reports[month] || {
          quarter: this.MONTHS[month - 1]?.value?.quarter,
          month: month,
          year: year,
          blank: true
        }
      }
      return result
    },
    getMonthToQuarterRelation(month, reports) {
      // Check if month's quarter is already calculated
      if (!month || month === null) return null
      const monthNum = Number(month)
      const quarter = 'Q' + Math.ceil(monthNum / 3)
      const quarterReport = reports[quarter]
      
      // Return the message if quarter is calculated and this month is blank
      if (quarterReport && !quarterReport.blank) {
        return quarter
      }
      return null
    },
    getQuarterToMonthsRelation(quarter, reports) {
      // Return all calculated full month names within a quarter
      if (!quarter || !quarter.match(/^Q[1-4]$/)) return null
      const quarterNum = Number(quarter.replace('Q', ''))
      const startMonth = (quarterNum - 1) * 3 + 1
      const endMonth = quarterNum * 3
      const names = []
      for (let m = startMonth; m <= endMonth; m++) {
        const monthReport = reports[m]
        if (monthReport && !monthReport.blank) {
          names.push(this.MONTHS[m - 1]?.fullText || `Month ${m}`)
        }
      }
      return names.length ? names : null
    },
    getMonthlyConflictWarning(month, reports) {
      const quarter = this.getMonthToQuarterRelation(month, reports)
      if (!quarter) return null
      return `${quarter} ${this.$gettext('report already calculated')}. ${this.$gettext('Switch view to quarterly to access it.')}`
    },
    getQuarterlyConflictWarning(quarter, reports) {
      const monthNames = this.getQuarterToMonthsRelation(quarter, reports)
      if (!monthNames || !monthNames.length) return null
      const plural = monthNames.length > 1
      const reportsWord = plural ? this.$gettext('reports') : this.$gettext('report')
      const pronoun = plural ? this.$gettext('them') : this.$gettext('it')
      return `${monthNames.join(', ')} ${reportsWord} ${this.$gettext('already calculated')}. ${this.$gettext('Switch view to monthly to access')} ${pronoun}.`
    },
    getMonthlyQuarterGroups(reports) {
      return [
        { quarter: 'Q1', months: [1, 2, 3] },
        { quarter: 'Q2', months: [4, 5, 6] },
        { quarter: 'Q3', months: [7, 8, 9] },
        { quarter: 'Q4', months: [10, 11, 12] },
      ].map((group) => ({
        key: group.quarter,
        quarter: group.quarter,
        monthly: true,
        reports: group.months
          .map((month) => reports[month])
          .filter((report) => !!report),
      }))
    },
    getDisplayGroups(reports) {
      if (this.reportViewMode === 'monthly') {
        return this.getMonthlyQuarterGroups(reports)
      }
      return [{ key: 'all', monthly: false, reports: this._orderedReports(reports) }]
    },
    isConflictingBlank(report, rawReports) {
      if (this.reportViewMode === 'monthly') {
        return !!this.getMonthToQuarterRelation(report.month, rawReports)
      }
      return !!this.getQuarterToMonthsRelation(report.quarter, rawReports)
    },
    hasConflictInGroup(group, rawReports) {
      return group.reports.some(report => report.blank && this.isConflictingBlank(report, rawReports))
    },
  },
}
</script>
<style lang="scss">
  .tax-report-list {
    .content {
      padding: 0 0.5rem;
      @media (min-width: '576px') {
        padding: 0 1rem;
      }

      .view-toggle-container {
        display: flex;
        align-items: center;
        margin: 1rem 0;

        .btn-group {
          display: flex;
        }
      }

      section {
        padding: 1rem 0;
        
        h2 {
          margin-bottom: 1rem;
          margin-top: 0.25rem;
          font-size: 1.5rem;
          color: #212529;
        }
      }

      .report-cards-wrap {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 1rem;
        margin-bottom: 0;

        &--monthly-group {
          grid-template-columns: repeat(3, minmax(0, 1fr));
          flex: 1;
        }
        
        @media (max-width: 1200px) {
          grid-template-columns: repeat(3, 1fr);

          &--monthly-group {
            grid-template-columns: repeat(3, minmax(0, 1fr));
          }
        }

        @media (max-width: 992px) {
          grid-template-columns: repeat(2, 1fr);

          &--monthly-group {
            grid-template-columns: repeat(3, minmax(0, 1fr));
          }
        }

        @media (max-width: 576px) {
          grid-template-columns: 1fr;

          &--monthly-group {
            grid-template-columns: 1fr;
          }
        }
      }

      .report-quarter-group {
        border-radius: 14px;
        padding: 0.45rem;
        margin-bottom: 1rem;
        display: flex;
        flex-direction: column;
        height: 100%;
        &--conflict {
          border-color: #f0c060;
          background: repeating-linear-gradient(
            -45deg,
            rgba(255, 253, 245, 0.6),
            rgba(255, 253, 245, 0.6) 8px,
            rgba(255, 248, 225, 0.6) 8px,
            rgba(255, 248, 225, 0.6) 16px
          );
        }
        &__title {
          font-size: 0.75rem;
          font-weight: 700;
          letter-spacing: 0.04em;
          color: $colorDraft800;
          margin: 0.1rem 0.2rem 0.45rem;
        }
      }

      @media (min-width: 1201px) {
        .report-quarter-groups-wrap {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 1rem;
        }

        .report-quarter-group {
          margin-bottom: 0;
        }
      }

      .report-card-item {
        height: 100%;
        display: flex;
      }

      .report-card {
        padding: 1.5rem;
        border-radius: 0.75rem;
        position: relative;
        border: 1px solid #e9ecef;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
        display: flex;
        flex-direction: column;
        height: 100%;
        text-decoration: none;
        transition: all 0.3s ease;
        background: #fff;
        cursor: pointer;
        width: 100%;

        &:hover {
          text-decoration: none;
          border: 1px solid $colorBlue;
          box-shadow: 0 4px 12px rgba($colorBlue, 0.15);

          .report-card__icon {
            color: $colorBlue;
            transform: scale(1.1);
          }
        }

        &--blank {
          border: 1px dashed #dee2e6;
          background: #fafbfc;

          &:hover {
            border-color: $colorBlue;
            background: $colorBlue50;

            .report-card__icon--add {
              color: $colorBlue;
            }
          }
        }

        &--conflict {
          border: 1px dashed #f0c060 !important;
          background: repeating-linear-gradient(
            -45deg,
            #fffdf5,
            #fffdf5 6px,
            #fff8e1 6px,
            #fff8e1 12px
          ) !important;
          cursor: not-allowed !important;
          pointer-events: none;

          &:hover {
            border-color: #f0c060 !important;
            background: repeating-linear-gradient(
              -45deg,
              #fffdf5,
              #fffdf5 6px,
              #fff8e1 6px,
              #fff8e1 12px
            ) !important;

            .report-card__icon {
              transform: none;
              color: #e6a817;
            }
          }
        }

        &__status {
          position: absolute;
          top: 1rem;
          right: 1rem;
          
          .status-badge {
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
            padding: 0.4rem 0.8rem;
            background: #fff3cd;
            border: 1px solid #ffc107;
            border-radius: 4px;
            font-size: 0.875rem;
            color: #856404;
          }
        }

        &__body {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          flex: 1;
        }

        &__icon {
          color: $colorBlue;
          font-size: 2rem;
          line-height: 1;
          display: inline-flex;
          transform-origin: left center;
          transition: all 0.3s ease;

          &--done {
            color: rgba($colorBrand, 0.5);
          }

          &--add {
            color: $colorBrand;
          }

        }

        &__title {
          color: #212529;
          font-size: 1.125rem;
          font-weight: 600;
          line-height: 1.3;
        }

        &__description {
          color: #6c757d;
          font-size: 0.875rem;
          line-height: 1.4;
        }

        &__relation-warning {
          margin-top: 0.35rem;
          color: #b07800;
          font-size: 0.8rem;
          font-weight: 500;
          line-height: 1.3;
        }

        &__total {
          margin-top: 1rem;
          padding-top: 0.75rem;
          border-top: 1px solid #e9ecef;
          color: #212529;
          font-size: 18px;
          font-weight: 700;
          line-height: 1.4;
        }

        &__total-label {
          color: #6c757d;
          margin-right: 0.5rem;
          font-weight: 500;
          font-size: 1rem;
        }

        &__actions {
          position: absolute;
          top: 0.5rem;
          right: 0.5rem;
          display: flex;
          gap: 0.5rem;
          flex-wrap: nowrap;
          align-items: center;
          opacity: 0;
          transition: opacity 0.3s ease;
          pointer-events: none;
        }

        &:hover .report-card__actions {
          opacity: 1;
          pointer-events: auto;
        }

        &__action-overflow {
          margin: 0;
          width: 32px;
          height: 32px;
          background: transparent;
          cursor: pointer;
          color: $colorTextBase;
          border-radius: calc(#{$gridBorderRadius} / 2);
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;

          &:hover {
            color: $colorBrand;
            background: #e8f7f3;
          }
        }

        &__action-overflow-toggle {
          background: none;
          padding: 0;
          border: 0;
          color: inherit;
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
          width: 100%;
          border-radius: calc(#{$gridBorderRadius} / 2);
          text-decoration: none;
          font-size: 1rem !important;
          .svg-inline--fa { margin: 0 !important; }
          &:hover,
          &:active,
          &:focus {
            background: transparent !important;
            color: $colorBrand !important;
            text-decoration: none;
          }

          &:after {
            display: none;
          }
        }
      }
    }
  }
</style>